import Vue from 'vue'
import { getAllWatch, getWatchDetails, getPart, getWatches, sendCart } from '@/api'
import { storeObject, getObject, removeObject } from '@/helpers/storage'
import router from '@/router';

const allowToRemoveOnLastItem = false;
const defaultFetch = { page: 1, force: false };

export default {
    namespaced: true,
    state: {
        watch: { parts: [] },
        showModal: false,
        paginated: {
            current: 1,
            data: [],
            total: 1,
            pageSize: 10,
            showQuickJumper: true
        },
        isLoading: false,
        cart: getObject('cart') || [],
        search: {
            type: '',
            content: ''
        },
        parts: {},
        loadingCart: false
    },
    mutations: {
        SET_WATCH: (state, watch) => state.watch = watch,
        SET_PAGINATED_WATCHES: (state, paginated) => state.paginated = paginated,
        ADD_TO_CART: (state, part) => state.cart.push(part),
        REMOVE_FROM_CART: (state, index) => state.cart.splice(index, 1),
        INCREMENT_PART_QUANTITY: (state, { index, quantity }) => state.cart[index].quantity = quantity,
        DECREMENT_PART_QUANTITY: (state, { index, quantity }) => state.cart[index].quantity = quantity,
        DISPOSE_CART: state => state.cart = [],
        SET_LOADING: (state) => state.isLoading = !state.isLoading,
        SET_QUANTITY_FOR_ITEM: (state, { index, quantity }) => state.cart[index].quantity = quantity,
        SET_PARTS: (state, parts) => state.parts = parts,
        SET_LOADING_CART: (state, loading = null) => state.loadingCart = loading !== null ? loading : !state.loadingCart,
        /**
         *
         * @param state
         * @param search
         * @returns {*}
         * @constructor
         * @description this mutation only exists to know which was
         * the last search type and keep it
         */
        SET_SEARCH: (state, search) => state.search = search
    },
    actions: {
        /**
         *
         * @param {function} commit
         * @param {object} state
         * @param {function} dispatch
         * @param {int} page
         * @param {boolean} force
         * @param {object} sorter
         * @description this action by defaults get all watches with its parts
         * paginated starting by the first page
         */
        fetch({ commit, state, dispatch }, { page, force, sorter = null } = defaultFetch) {
            if (state.search.content) {
                if (state.search.type === 'watch') {
                    dispatch('getWatches', { name: state.search.content, page})
                } else {
                    dispatch('getPart', { part: state.search.content, page})
                }
            } else {
                if (force || !state.paginated.data.length) {
                    commit('SET_LOADING');

                    let query = {};

                    if (sorter !== null && Object.keys(sorter).length > 0) {
                        let prefix = 'parts';
                        query = { sort: sorter.field, dord: sorter.order === 'ascend' ? 'asc' : 'desc' };

                        if (sorter.field === 'name') {
                            prefix = 'watches';
                        }

                        query.sort = `${prefix}.${query.sort}`;
                    }

                    /**
                     * if everything is set, the pagination object should look like this
                     * {
                     *     sort: `[watches|parts].column`,
                     *     dord: `asc`|`desc`
                     * }
                     */

                    getAllWatch(page, query).then(res => {
                        commit('SET_PAGINATED_WATCHES', {
                            ...res.data,
                            current: res.data.current_page,
                            pageSize: res.data.per_page,
                            data: res.data.data.map(watch => ({
                                ...watch,
                                parts: [],
                            }))
                        });

                    }).finally(() => commit('SET_LOADING'))
                }
            }
        },

        /**
         *
         * @param {function} commit
         * @param {object} state
         * @param {object} watch
         * @param {boolean} force
         */
        getWatchDetails({ commit, state }, { watch, force = false}) {
            commit('SET_LOADING');
            getWatchDetails(watch.watch_id)
                .then(res => {
                    commit('SET_PARTS', res.data);
            }).finally(() => commit('SET_LOADING'))
        },

        /**
         *
         * @param {function} commit
         * @param {function} dispatch
         * @param {int} part
         * @param {int} page
         */
        getPart({ commit, dispatch }, { part, page }) {
            commit('SET_SEARCH', { type: 'part', content: part });

            if (!part) {
                return dispatch('fetch', {...defaultFetch, force: true})
            } else {
                commit('SET_LOADING');

                return getPart(page, part)
                    .then(res => {

                        commit('SET_PAGINATED_WATCHES', {
                            ...res.data,
                            current: res.data.current_page,
                            pageSize: res.data.per_page,
                            data: res.data.data.map(watch => ({
                                ...watch,
                                parts: [],
                                ...watch
                            }))
                        })

                        router.replace({ query: { part, page } });
                }).finally(() => commit('SET_LOADING'))
            }
        },

        /**
         *
         * @param {function} commit
         * @param {function} dispatch
         * @param {string} name
         * @param {int} page
         */
        getWatches({ commit, dispatch }, { name, page }) {
            commit('SET_SEARCH', { type: 'watch', content: name });
            if (!name) {
                return dispatch('fetch', {...defaultFetch, force: true})
            } else {
                commit('SET_LOADING');
                return getWatches(page, name)
                    .then(res => {

                        commit('SET_PAGINATED_WATCHES', {
                            ...res.data,
                            current: res.data.current_page,
                            pageSize: res.data.per_page,
                            data: res.data.data.map(watch => ({
                                ...watch,
                                parts: [],
                                name: watch.name,
                            }))
                        })

                        router.replace({ query: { name, page } });
                }).finally(() => commit('SET_LOADING'))
            }
        },

        /**
         *
         * @param {function} commit
         * @param {object} state
         * @param {string} part
         * @param {string} field
         * @param {object} source
         * @param {object} rest
         */
        addToCart({ commit, state }, { part, field, source, ...rest }) {
            // console.log(part, field, source)
            let exists = state.cart.some(item => item.name === part);

            if (!exists) {
                let item = { name: part, field, quantity: 1, watch_id: source.watch_id, ...rest };
                // console.log(rest)

                commit('ADD_TO_CART', item);
                storeObject('cart', state.cart)
            } else {
                let index = state.cart.findIndex(item => item.name === part);
                let quantity = state.cart[index].quantity;
                quantity++;
                commit('INCREMENT_PART_QUANTITY', { index, quantity });
                storeObject('cart', state.cart)
            }
        },

        /**
         *
         * @param {function} commit
         * @param {object} state
         * @param {int} index
         */
        removeFromCart({ commit, state }, index) {
            commit('REMOVE_FROM_CART', index);
            storeObject('cart', state.cart)
        },

        /**
         *
         * @param {function} commit
         * @param {object} state
         * @param {int} index
         */
        incrementPartQuantity({ commit, state}, index) {
            let quantity = state.cart[index].quantity;
            quantity++;
            commit('INCREMENT_PART_QUANTITY', { index, quantity });
            storeObject('cart', state.cart)
        },

        /**
         *
         * @param {function} commit
         * @param {object} state
         * @param {int} index
         */
        decrementPartQuantity({ commit, state}, index) {
            let quantity = state.cart[index].quantity;
            if (quantity > 1) {
                quantity--;
                commit('DECREMENT_PART_QUANTITY', { index, quantity });
                storeObject('cart', state.cart)
            } else {
                if (allowToRemoveOnLastItem) {
                    commit('REMOVE_FROM_CART', index)
                }
            }
        },

        /**
         *
         * @param {function} commit
         * @param {object} state
         * @param {function} dispatch
         * @param {string} item
         * @param {int} quantity
         */
        setQuantityForItem({ commit, state, dispatch }, { item, quantity }) {
            let found = state.cart.findIndex(el => {
                return el.name === item;
            });

            // if (found >= 0) {
            //     if (quantity === 0 || !quantity) {
            //         commit('REMOVE_FROM_CART', found)
            //     } else {
            //         commit('SET_QUANTITY_FOR_ITEM', { index: found, quantity: parseInt(quantity) })
            //     }
            //     storeObject('cart', state.cart)
            // }
            // console.log(quantity)
            if (found >= 0 && quantity) {
                commit('SET_QUANTITY_FOR_ITEM', { index: found, quantity: parseInt(quantity) })
                storeObject('cart', state.cart)
            }
        },

        /**
         *
         * @param {function} commit
         * @param {object} state
         * @param {{ messages: { responses: { order_sent: string } } }} lang
         */
        send({ commit, state, rootGetters: { lang } }, query = {}) {
            commit('SET_LOADING_CART')

            sendCart(state.cart, query.note, query.target_username)
                .then(res => {

                    removeObject('cart');
                    commit('DISPOSE_CART');

                    Vue.prototype.$notification.open({
                        message: 'Éxito!',
                        description: lang.messages.responses.order_sent.replace("#match#", res.data[0].id),
                        icon: (h) => (<a-icon type="smile" style="color: #48BE83" />),
                    });
            })
            .finally(() => commit('SET_LOADING_CART'))
        }
    },
    getters: {
        /**
         *
         * @param {object} state
         * @returns {number}
         */
        itemsInCart: state => state.cart.length,

        /**
         *
         * @param {object} state
         * @returns {T | * | number}
         */
        totalByEachItemInCart: state => state.cart.reduce((a, b) => a + b.quantity || 0, 0),

        /**
         *
         * @param {object} state
         * @param {object} getters
         * @param {object} rootState
         * @param {object} rootGetters
         * @returns {({scopedSlots: {customRender: string}, sorter: boolean, width: number, title: *, align: string, key: string}|{scopedSlots: {customRender: string}, sorter: boolean, dataIndex: string, width: number, title: *, key: string}|{scopedSlots: {customRender: string}, sorter: boolean, dataIndex: string, width: number, title: ({test: function(*=, *): boolean}|string), key: string}|{scopedSlots: {customRender: string}, sorter: boolean, dataIndex: string, width: number, title: *, key: string}|{scopedSlots: {customRender: string}, sorter: boolean, dataIndex: string, width: number, title: *, key: string})[]}
         */
        fields: (state, getters, rootState, rootGetters) => {
            /**
             * @var {{ picture, name, case, case_f, bra_strap, crown, crystal_set, dial_c, gasket, hands, movement, watch: { main_table } }} lang
             */
            const lang = rootGetters.lang.watch.main_table;
            return [
                { title: lang.picture,  key: 'picture', align: 'center', scopedSlots: { customRender: 'picture' },  sorter: false, width: 128 },
                { title: lang.name, dataIndex: 'name', key: 'name', scopedSlots: { customRender: 'name' }, sorter: true, width: 110 },
                { title: 'Marca', dataIndex: 'mark_name', key: 'mark_name', scopedSlots: { customRender: 'mark_name' }, sorter: true, width: 110 },
                { title: lang.family_name, dataIndex: 'family_name', key: 'family_name', scopedSlots: { customRender: 'family_name' }, sorter: false, width: 180 },
                { title: lang.case_f, dataIndex: 'case_f', key: 'case_f', scopedSlots: { customRender: 'case_f' }, sorter: true, width: 160 },
                { title: lang.case, dataIndex: 'case', key: 'case', scopedSlots: { customRender: 'case' }, sorter: true, width: 180 },
                { title: lang.gasket, dataIndex: 'gasket', key: 'gasket', scopedSlots: { customRender: 'gasket' }, sorter: true, width: 180 },
                { title: lang.gasket_glass, dataIndex: 'gasket_glass', key: 'gasket_glass', scopedSlots: { customRender: 'gasket_glass' }, sorter: true, width: 180 },
                { title: lang.bra_strap, dataIndex: 'bra_strap', key: 'bra_strap', scopedSlots: { customRender: 'bra_strap' }, sorter: true, width: 160 },
                { title: lang.crown, dataIndex: 'crown', key: 'crown', scopedSlots: { customRender: 'crown' }, sorter: true, width: 160 },
                { title: lang.crystal_set, dataIndex: 'crystal_set', key: 'crystal_set', scopedSlots: { customRender: 'crystal_set' }, sorter: true, width: 160 },
                { title: lang.dial, dataIndex: 'dial', key: 'dial', scopedSlots: { customRender: 'dial' }, sorter: true, width: 130 },
                // { title: lang.gasket, dataIndex: 'gasket', key: 'gasket', scopedSlots: { customRender: 'gasket' }, sorter: true, width: 130 },
                { title: lang.hands, dataIndex: 'hands', key: 'hands', scopedSlots: { customRender: 'hands' }, sorter: true, width: 130 },
                { title: lang.movement, dataIndex: 'movement', key: 'movement', scopedSlots: { customRender: 'movement' }, sorter: true, width: 130 },
                { title: lang.bezel, dataIndex: 'bezel', key: 'bezel', scopedSlots: { customRender: 'bezel' }, sorter: true, width: 130 },
                { title: lang.upper_dial, dataIndex: 'upper_dial', key: 'upper_dial', scopedSlots: { customRender: 'upper_dial' }, sorter: true, width: 130 },
                { title: lang.clasp, dataIndex: 'clasp', key: 'clasp', scopedSlots: { customRender: 'clasp' }, sorter: true, width: 130 },
                { title: lang.ext_link, dataIndex: 'ext_link', key: 'ext_link', scopedSlots: { customRender: 'ext_link' }, sorter: true, width: 130 },
            ]
        },

        /**
         *
         * @param {object} state
         * @param {object} getters
         * @param {object} rootState
         * @param {object} rootGetters
         * @returns {({scopedSlots: {customRender: string}, dataIndex: string, title: ({test: (function(*=, *): boolean)}|string)}|{scopedSlots: {customRender: string}, dataIndex: string, title}|{scopedSlots: {customRender: string}, dataIndex: string, title}|{scopedSlots: {customRender: string}, dataIndex: string, title}|{scopedSlots: {customRender: string}, dataIndex: string, title})[]}
         */
        modalFields: (state, getters, rootState, rootGetters) => {
            const lang = rootGetters.lang.watch.main_table;

            return [
                { title: 'Marca', dataIndex: "mark_name", scopedSlots: {customRender: 'mark_name' } },
                { title: lang.case, dataIndex: "case", scopedSlots: {customRender: 'case' } },
                { title: lang.case_f, dataIndex: "case_f", scopedSlots: {customRender: 'case_f' } },
                { title: lang.family_name, dataIndex: 'family_name', key: 'family_name', scopedSlots: { customRender: 'family_name' }, sorter: false, width: 180 },
                { title: lang.bra_strap, dataIndex: "bra_strap", scopedSlots: {customRender: 'bra_strap' } },
                { title: lang.crown, dataIndex: "crown", scopedSlots: {customRender: 'crown' } },
                { title: lang.crystal_set, dataIndex: "crystal_set", scopedSlots: {customRender: 'crystal_set' } },
                { title: lang.dial, dataIndex: "dial", scopedSlots: {customRender: 'dial' } },
                { title: lang.gasket, dataIndex: "gasket", scopedSlots: {customRender: 'gasket' } },
                { title: lang.gasket_glass, dataIndex: 'gasket_glass', key: 'gasket_glass', scopedSlots: { customRender: 'gasket_glass' }, sorter: true, width: 180 },
                { title: lang.hands, dataIndex: "hands", scopedSlots: {customRender: 'hands' } },
                { title: lang.movement, dataIndex: "movement", scopedSlots: {customRender: 'movement' } },
                { title: lang.bezel, dataIndex: "bezel", scopedSlots: {customRender: 'bezel' } },
                { title: lang.upper_dial, dataIndex: "upper_dial", scopedSlots: {customRender: 'upper_dial' } },
                { title: lang.clasp, dataIndex: "clasp", scopedSlots: {customRender: 'clasp' } },
                { title: lang.ext_link, dataIndex: "ext_link", scopedSlots: {customRender: 'ext_link' } },
            ]
        },
        /**
         *
         * @param {object} state
         * @returns {int}
         */
        totalInCartByItem: state => item => {
            let found = state.cart.find(el => el.name === item);
            if (found) return found.quantity;
            return 0;
        },

        /**
         *
         * @param {object} state
         * @returns {function(*): number}
         */
        indexForFoundPartInCart: state => item => {
            return state.cart.findIndex(el => el.name === item)
        },

        /**
         *
         * @param {object} state
         * @returns {function(*=): *[]}
         */
        partsForWatch: state => field => [state.parts]
            .filter(_part => Object.keys(_part)
                .some(p => p === field.dataIndex || field.key))
            .map(p => ({
                name: p[field.dataIndex] || p[field.key],
                watch: p.name,
                ...p
            }))
            .filter(part => part.name)
    }
}
